import {
    moveToFolderCreateNewAriaLabel,
    moveToFolderCreateNewTooltip,
    moveToFolderAddFolderNameText,
} from './NewFolderMenuItem.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47749 */
import { MoveToActionSource } from '../datapoints';
import isFolderNameValid from '../helpers/isFolderNameValid';
import { getNewFolderTextFromFindText } from '../helpers/moveToStringUtils';
import menuStore from '../store/store';
import { observer } from 'mobx-react';
import { ITextField } from 'office-ui-fabric-react/lib/components/TextField';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { getRTL } from 'office-ui-fabric-react/lib/Utilities';
import { ControlIcons } from 'owa-control-icons';

import * as React from 'react';
import {
    onNewFolderTextInvalidated,
    onNewFolderTextChanged,
    resetNewFolderViewState,
} from '../actions/internalActions';

const styles = require('./NewFolderMenuItem.scss');
const classNames = require('classnames/bind').bind(styles);

export interface NewFolderMenuItemProps {
    isEditMode: boolean;
    containerClass?: string;
    createNew: (name: string, actionSource: MoveToActionSource) => void;
}

@observer
export default class NewFolderMenuItem extends React.Component<NewFolderMenuItemProps, {}> {
    private textField: ITextField;

    render() {
        const { newFolderViewState } = menuStore;

        // Case 1: Show Create New
        // Case 2: Show New Folder when in edit mode
        // Resetting newFolderViewState to null is also a value change and causes
        // re-render. Therefore check for null and early return.
        if (this.props.isEditMode) {
            if (newFolderViewState) {
                return this.renderNewFolderEditMenu();
            } else {
                return null;
            }
        } else {
            return this.renderCreateNewMenu();
        }
    }

    private renderCreateNewMenu = () => {
        const findText = menuStore.findText;
        const ariaLabel = loc(moveToFolderCreateNewAriaLabel);
        return (
            <div
                aria-label={ariaLabel}
                className={classNames(styles.newFolderMenuItemContainer, this.props.containerClass)}
                title={loc(moveToFolderCreateNewTooltip)}
                data-is-focusable={true}
                onKeyDown={ev => this.onKeyDownOnCreateNewFolder(ev, findText)}
                onClick={ev => this.onCreateNewFolderClicked(ev, findText)}
                role={'menuitem'}>
                {this.renderCreateNewIcon()}
                {this.renderCreateNew()}
            </div>
        );
    };

    private renderNewFolderEditMenu = () => {
        const { newFolderViewState } = menuStore;
        const newFolderInternalContainerClass = classNames(
            styles.newFolderMenuItemContainerInternal,
            { isInvalid: newFolderViewState.isInvalid }
        );
        return (
            <div
                className={styles.newFolderMenuItemContainer}
                data-is-focusable={true}
                role={'menuitem'}>
                <div className={newFolderInternalContainerClass}>
                    {this.renderCreateNewIcon()}
                    {this.renderNewFolderTextField()}
                </div>
            </div>
        );
    };

    /**
     * Render the text field to input the new folder name
     */
    private renderNewFolderTextField = (): JSX.Element => {
        return (
            <TextField
                autoFocus={true}
                borderless={true}
                componentRef={ref => (this.textField = ref)}
                className={styles.newFolderTextField}
                inputClassName={styles.newFolderTextInput}
                onChange={onNewFolderTextChanged}
                onKeyDown={this.onKeyDownOnNewTextField}
                placeholder={loc(moveToFolderAddFolderNameText)}
                value={menuStore.newFolderViewState.folderText}
                ariaLabel={loc(moveToFolderAddFolderNameText)}
            />
        );
    };

    /**
     * Handler key down event on the TextField
     * OnEnter- call commit new folder (calls create from props)
     * OnEscape- resetNewFolderViewState, which will dismiss *only* the new folder menu
     */
    private onKeyDownOnNewTextField = (event: React.KeyboardEvent<unknown>) => {
        switch (event.keyCode) {
            case KeyCodes.left:
                // Stop propagation on left key when user typed in text field to prevent dismiss the folder submenu
                if (!getRTL() && this.textField.value) {
                    event.stopPropagation();
                }
                break;

            case KeyCodes.right:
                // Stop propagation on right key for RTL when user typed in text field to prevent dismiss the folder submenu
                if (getRTL() && this.textField.value) {
                    event.stopPropagation();
                }
                break;

            case KeyCodes.enter:
                this.commitNewFolder(this.textField.value, 'NewFolderItem');
                break;

            case KeyCodes.escape:
                event.stopPropagation();
                resetNewFolderViewState();
                break;
        }
    };

    /**
     * Render the folder find text and the (Create new) text
     */
    private renderCreateNew(): JSX.Element {
        const findText = menuStore.findText;
        return (
            <div className={styles.createNewTextContainer}>
                <div className={styles.createNewFolderMatchText}>{'"' + findText + '"'}</div>
                <div className={styles.createNewFolderText}>
                    &nbsp;{getNewFolderTextFromFindText(menuStore.moveToMailboxContext)}
                </div>
            </div>
        );
    }

    /**
     * Render simple icon for a create new menu
     */
    private renderCreateNewIcon = (): JSX.Element => {
        return <Icon className={styles.newFolderIcon} iconName={ControlIcons.FabricFolder} />;
    };

    /**
     * On key down on create new folder menu
     */
    private onKeyDownOnCreateNewFolder = (ev: React.KeyboardEvent<unknown>, findText: string) => {
        if (ev.keyCode === KeyCodes.enter) {
            ev.stopPropagation();
            this.commitNewFolder(findText, 'SearchCreateNew');
        }
    };

    /**
     * On click on create new folder menu
     */
    private onCreateNewFolderClicked = (ev: React.MouseEvent<unknown>, findText: string) => {
        ev.stopPropagation();
        this.commitNewFolder(findText, 'SearchCreateNew');
    };

    private commitNewFolder = (folderName: string, actionSource: MoveToActionSource) => {
        if (isFolderNameValid(menuStore.moveToMailboxContext, folderName)) {
            this.props.createNew(folderName, actionSource);
            resetNewFolderViewState();
        } else {
            onNewFolderTextInvalidated();
        }
    };
}
